import React, { useCallback, useEffect, useMemo, useState } from "react";
import TableSkeleton from "../../../../../component/TableSkeleton";
import { Box, Stack, Table, TableHead, TableBody, TableRow, TableCell, CircularProgress } from "@mui/material";
import TopBar from "../../../../../component/TopBar";
import EmptyState from "../../../../../component/EmptyState";
import BtnPrimary from "../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../component/SecondaryButton";
import { StatusChip, headCellSx, bodyCellSx } from "../../../../../component/listing";
import { useNotify } from "../../../../../component/NotificationProvider";
import { getOrgEmployees } from "../../../../../services/globalData";
import { getTeamLeaveRequests, reviewLeaveRequest } from "../../../../../services/leave";
import { getTeamTimesheets, reviewTimesheet } from "../../../../../services/timesheet";
import { getTeamAttendanceRequests, reviewAttendanceRequest } from "../../../../../services/attendanceRequest";
import { getEmployeeGoals, getReviewCycles } from "../../../../../services/performance";
import GoalFormDialog from "../../../../../component/performance/GoalFormDialog";
import { getTokenInfo } from "../../../../../services/api";
import { tokens, radius } from "../../../../../theme";

import { pressable } from "../../../../../component/pressableProps";
/**
 * What this manager has been asked to decide.
 *
 * Approval rights arrived before anywhere to exercise them — a manager could
 * act on an item by id and had no way to find one. This is the queue: only the
 * things actually waiting, across the three kinds, with the decision on the row
 * rather than a screen away.
 */

const KINDS = [
  { key: "leave", label: "Leave", pending: "Pending" },
  { key: "timesheet", label: "Timesheets", pending: "pending" },
  { key: "attendance", label: "Attendance corrections", pending: "Pending" },
];

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "—");

const TeamApprovals = () => {
  const notify = useNotify();
  const [items, setItems] = useState({ leave: [], timesheet: [], attendance: [] });
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null); // `${kind}:${id}` currently submitting
  const [tab, setTab] = useState("approvals");
  const [goalsByEmployee, setGoalsByEmployee] = useState({});
  const [cycles, setCycles] = useState([]);
  const [goalDialog, setGoalDialog] = useState(null); // { employeeId, goal }

  // the directory the shell loaded already says who reports to whom
  const myReports = useMemo(() => {
    const me = getTokenInfo()?.accessTokenData?.employeeId;
    return (getOrgEmployees() || []).filter((e) => String(e.reportingTo || "") === String(me));
  }, []);

  const loadGoals = useCallback(async () => {
    const entries = await Promise.all(
      myReports.map(async (r) => {
        const res = await getEmployeeGoals(r._id).catch(() => null);
        return [r._id, Array.isArray(res?.data) ? res.data : []];
      })
    );
    setGoalsByEmployee(Object.fromEntries(entries));
  }, [myReports]);

  useEffect(() => {
    if (tab !== "goals") return;
    loadGoals();
    getReviewCycles().then((c) => setCycles(Array.isArray(c?.data) ? c.data : [])).catch(() => {});
  }, [tab, loadGoals]);

  // names resolve from the directory the shell already loaded — the projection
  // carries fname/lname, so a manager's queue costs no extra lookup
  const nameById = useMemo(() => {
    const map = {};
    (getOrgEmployees() || []).forEach((e) => {
      map[e._id] = `${e.fname || ""} ${e.lname || ""}`.trim();
    });
    return map;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const [leave, timesheet, attendance] = await Promise.all([
      getTeamLeaveRequests("Pending").catch(() => null),
      getTeamTimesheets({ status: "pending" }).catch(() => null),
      getTeamAttendanceRequests("Pending").catch(() => null),
    ]);
    setItems({
      leave: Array.isArray(leave?.data) ? leave.data : [],
      timesheet: Array.isArray(timesheet?.data) ? timesheet.data : [],
      attendance: Array.isArray(attendance?.data) ? attendance.data : [],
    });
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const decide = async (kind, id, approved) => {
    setActing(`${kind}:${id}`);
    try {
      let res;
      if (kind === "leave") {
        res = await reviewLeaveRequest(
          { status: approved ? "Approved" : "Rejected", reviewerComment: approved ? "Approved" : "Rejected" },
          id
        );
      } else if (kind === "timesheet") {
        res = await reviewTimesheet(id, { status: approved ? "approved" : "rejected" });
      } else {
        res = await reviewAttendanceRequest(id, {
          status: approved ? "Approved" : "Rejected",
          reviewerComment: approved ? "Approved" : "Rejected",
        });
      }
      if (res?.success === false) throw new Error(res?.message || "Could not record the decision");
      notify.success(approved ? "Approved" : "Rejected");
      // reload rather than splice: approving leave moves a balance, and the
      // next decision should be made against what the server now holds
      await load();
    } catch (err) {
      notify.error(err?.message || "Could not record the decision");
    } finally {
      setActing(null);
    }
  };

  const total = items.leave.length + items.timesheet.length + items.attendance.length;

  const rowsFor = (kind) => {
    if (kind === "leave") {
      return items.leave.map((r) => ({
        id: r._id,
        who: nameById[r.employeeId] || "—",
        detail: `${r.days?.length || 0} day(s)`,
        when: fmtDate(r.firstDay || r.createdAt),
        note: r.reason || "",
      }));
    }
    if (kind === "timesheet") {
      return items.timesheet.map((t) => ({
        id: t._id,
        who: nameById[t.employee] || "—",
        detail: `${t.totalHours || 0} hrs`,
        when: `${fmtDate(t.startDate)} – ${fmtDate(t.endDate)}`,
        note: "",
      }));
    }
    return items.attendance.map((a) => ({
      id: a._id,
      who: nameById[a.employeeId] || "—",
      detail: a.type === "SHIFT_CHANGE" ? "Shift change" : "Regularization",
      when: a.dayString || fmtDate(a.createdAt),
      note: a.reason || "",
    }));
  };

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar MainHeading="My team" breadcrumbs={[{ label: "My team" }]} />

      {/* Goals sit here because a manager has no People section to reach a
          report's profile through — approvals and goals are both "things I do
          for my team", and this is the only page that is theirs. */}
      <Stack direction="row" spacing={0.5} sx={{ background: tokens.surfaceSubtle, p: "0.25rem", borderRadius: "10px", mb: "1.25rem", width: "fit-content" }}>
        {[["approvals", "Approvals", total], ["goals", "Goals", null]].map(([k, label, count]) => (
          <Box
            key={k}
            {...pressable(() => setTab(k))}
            sx={{
              cursor: "pointer", px: "1rem", py: "0.5rem", borderRadius: "8px",
              fontSize: "0.875rem", fontWeight: 600,
              background: tab === k ? tokens.surface : "transparent",
              color: tab === k ? tokens.text : tokens.textMuted,
              boxShadow: tab === k ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
            }}
          >
            {label}
            {count != null && <Box component="span" sx={{ color: tokens.textMuted, fontWeight: 500 }}> · {count}</Box>}
          </Box>
        ))}
      </Stack>

      {tab === "goals" ? (
        myReports.length === 0 ? (
          <EmptyState title="No direct reports" description="People who report to you appear here." />
        ) : (
          <Stack spacing={2}>
            {myReports.map((r) => {
              const goals = goalsByEmployee[r._id] || [];
              return (
                <Box key={r._id} sx={{ border: `1px solid ${tokens.border}`, borderRadius: `${radius.card}px`, background: tokens.card, overflow: "hidden" }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: "1rem 1.25rem", borderBottom: `1px solid ${tokens.border}` }}>
                    <Box sx={{ fontSize: "0.9375rem", fontWeight: 600 }}>
                      {`${r.fname || ""} ${r.lname || ""}`.trim()}
                      <Box component="span" sx={{ fontSize: "0.8125rem", color: tokens.textMuted, fontWeight: 500 }}> · {goals.length} goal{goals.length === 1 ? "" : "s"}</Box>
                    </Box>
                    <BtnSecondary
                      onClick={() => setGoalDialog({ employeeId: r._id, goal: null })}
                      sx={{ height: "2.25rem", px: "1rem", borderRadius: "8px", fontSize: "0.8125rem" }}
                    >
                      Add goal
                    </BtnSecondary>
                  </Stack>

                  {goals.length === 0 ? (
                    <Box sx={{ p: "1.25rem", fontSize: "0.8125rem", color: tokens.textMuted }}>
                      No goals set yet.
                    </Box>
                  ) : (
                    <Box sx={{ p: "0.75rem 1.25rem" }}>
                      {goals.map((g) => (
                        <Stack key={g._id} direction="row" alignItems="center" spacing={1.5} sx={{ py: "0.75rem", borderBottom: `1px solid ${tokens.surfaceSubtle}` }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ fontSize: "0.875rem", fontWeight: 600, color: tokens.text }}>{g.objective}</Box>
                            <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>
                              Weight {g.weight ?? 1}
                              {g.reviewCycle?.name ? ` · ${g.reviewCycle.name}` : " · no cycle"}
                              {g.locked?.definition ? " · fixed for the cycle" : ""}
                            </Box>
                          </Box>
                          <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, minWidth: "3rem", textAlign: "right" }}>{g.progress || 0}%</Box>
                          <BtnSecondary
                            onClick={() => setGoalDialog({ employeeId: r._id, goal: g })}
                            disabled={Boolean(g.locked?.definition)}
                            sx={{ height: "2rem", px: "1rem", borderRadius: "8px", fontSize: "0.8125rem" }}
                          >
                            {g.locked?.definition ? "Locked" : "Edit"}
                          </BtnSecondary>
                        </Stack>
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Stack>
        )
      ) : loading ? (
        (
          /* The header and the column widths are known before the data
             is, so they render immediately and nothing shifts when the
             rows land. */
          <Table sx={{ width: "100%" }}>
            <TableHead>
                    <TableRow>
                      <TableCell sx={headCellSx}>Employee</TableCell>
                      <TableCell sx={headCellSx}>Detail</TableCell>
                      <TableCell sx={headCellSx}>When</TableCell>
                      <TableCell sx={headCellSx}>Reason</TableCell>
                      <TableCell sx={{ ...headCellSx, width: "13rem" }} align="right">Decision</TableCell>
                    </TableRow>
                  </TableHead>
            <TableBody><TableSkeleton columns={5} /></TableBody>
          </Table>
        )
      ) : total === 0 ? (
        <EmptyState
          title="Nothing waiting on you"
          description="Leave, timesheets and attendance corrections from your reports appear here while they need a decision."
        />
      ) : (
        <Stack spacing={3}>
          {KINDS.map(({ key, label }) => {
            const rows = rowsFor(key);
            if (!rows.length) return null;
            return (
              <Box
                key={key}
                sx={{
                  border: `1px solid ${tokens.border}`,
                  borderRadius: `${radius.card}px`,
                  background: tokens.card,
                  overflow: "hidden",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ p: "1rem 1.25rem", borderBottom: `1px solid ${tokens.border}` }}
                >
                  <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text }}>{label}</Box>
                  <StatusChip label={`${rows.length} waiting`} tone="warning" />
                </Stack>

                <Table sx={{ width: "100%" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headCellSx}>Employee</TableCell>
                      <TableCell sx={headCellSx}>Detail</TableCell>
                      <TableCell sx={headCellSx}>When</TableCell>
                      <TableCell sx={headCellSx}>Reason</TableCell>
                      <TableCell sx={{ ...headCellSx, width: "13rem" }} align="right">Decision</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((r) => {
                      const busy = acting === `${key}:${r.id}`;
                      return (
                        <TableRow key={r.id} hover>
                          <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{r.who}</TableCell>
                          <TableCell sx={bodyCellSx}>{r.detail}</TableCell>
                          <TableCell sx={{ ...bodyCellSx, whiteSpace: "nowrap" }}>{r.when}</TableCell>
                          <TableCell sx={{ ...bodyCellSx, color: tokens.textMuted }}>{r.note || "—"}</TableCell>
                          <TableCell sx={bodyCellSx} align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <BtnSecondary
                                onClick={() => decide(key, r.id, false)}
                                disabled={Boolean(acting)}
                                sx={{ height: "2.25rem", px: "1rem", borderRadius: "8px", fontSize: "0.8125rem", color: tokens.danger, borderColor: tokens.dangerBorder }}
                              >
                                Reject
                              </BtnSecondary>
                              <BtnPrimary
                                onClick={() => decide(key, r.id, true)}
                                disabled={Boolean(acting)}
                                sx={{ height: "2.25rem", px: "1.25rem", borderRadius: "8px", fontSize: "0.8125rem" }}
                              >
                                {busy ? <CircularProgress size={15} sx={{ color: "#fff" }} /> : "Approve"}
                              </BtnPrimary>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            );
          })}
        </Stack>
      )}

      <GoalFormDialog
        open={Boolean(goalDialog)}
        goal={goalDialog?.goal}
        employeeId={goalDialog?.employeeId}
        cycles={cycles}
        notify={notify}
        onClose={() => setGoalDialog(null)}
        onSaved={loadGoals}
      />
    </Box>
  );
};

export default TeamApprovals;
